/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Reset Password Page
 *
 * UX S303: Added password visibility toggles on both fields so users can
 * verify what they typed before submitting, reducing "Passwords do not match"
 * errors. Added a password strength indicator so users know why a password
 * is rejected before they submit. Missing-token guard added so users who
 * arrive without a token see a clear message instead of a silent failure.
 */

import React, { useState } from "react";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useLangPrefix } from "@/hooks/use-lang-prefix";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { authClient } from "@/lib/auth-client";
import { useSearchParams } from "react-router-dom";
import { logger } from "@/lib/logger";
import { Eye, EyeOff } from "lucide-react";
import { useDocumentTitle } from "@/hooks/use-document-title";

const schema = z
  .object({
    password: z.string().trim().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string().trim(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type FormData = z.infer<typeof schema>;

/** Visible password input with a show/hide toggle button. */
function PasswordInput({
  id,
  placeholder,
  registerProps,
  error,
}: {
  id: string;
  placeholder: string;
  registerProps: ReturnType<ReturnType<typeof useForm<FormData>>["register"]>;
  error?: string;
}) {
  const [show, setShow] = useState(false);
  const errorId = `${id}-error`;
  return (
    <div>
      <div className="relative">
        <input
          id={id}
          type={show ? "text" : "password"}
          placeholder={placeholder}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          {...registerProps}
          className="w-full p-2.5 pr-10 border border-gray-300 rounded-md text-base outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
      {error && (
        <p id={errorId} className="text-red-500 text-sm mt-1" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

const ResetPasswordPage = () => {
  useDocumentTitle("Reset Password");
  const { dashboardPath, pagePath } = useLangPrefix();
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = (data: FormData) => {
    setServerError(null);
    authClient.resetPassword(
      {
        newPassword: data.password,
        token: token || "",
      },
      {
        onSuccess: () => {
          setSuccess(true);
        },
        onError: (ctx) => {
          setServerError(ctx.error?.message || "Something went wrong. Please try again or request a new reset link.");
        },
      },
    );
  };

  const handleGoToDashboard = () => {
    window.location.href = dashboardPath("overview");
  };

  // Guard: if there is no token the reset cannot succeed — show a clear message.
  if (!token) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-md p-8 w-full max-w-md m-4 text-center">
          <div className="bg-yellow-100 rounded-full w-16 h-16 flex items-center justify-center mb-4 mx-auto">
            <svg width="40" height="40" fill="none" viewBox="0 0 24 24">
              <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-yellow-500" />
            </svg>
          </div>
          <h2 className="font-bold text-2xl mb-2">Invalid Reset Link</h2>
          <p className="text-gray-500 text-sm mb-6">
            This password reset link is missing or has expired. Please request a new one from the login page.
          </p>
          <button
            type="button"
            onClick={() => window.location.href = pagePath("")}
            className="w-full bg-primary text-primary-foreground rounded-md py-3 font-semibold text-base hover:bg-btn-primary-hover active:bg-btn-primary-selected transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-md p-8 w-full max-w-md m-4">
        {!success ? (
          <form onSubmit={handleSubmit(onSubmit)} className="w-full">
            <h2 className="font-bold text-2xl mb-2 text-center">
              Reset Your Password
            </h2>
            <p className="text-gray-500 text-sm mb-6 text-center">
              Create a strong new password to secure your account.
            </p>

            {serverError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4 text-sm text-red-700">
                {serverError}
              </div>
            )}

            <div className="w-full mb-4">
              <label htmlFor="reset-password" className="font-semibold text-sm mb-2 block">
                New Password
              </label>
              <PasswordInput
                id="reset-password"
                placeholder="Enter your new password"
                registerProps={register("password")}
                error={errors.password?.message}
              />
            </div>

            <div className="w-full mb-6">
              <label htmlFor="reset-confirm-password" className="font-semibold text-sm mb-2 block">
                Confirm Password
              </label>
              <PasswordInput
                id="reset-confirm-password"
                placeholder="Type your new password again"
                registerProps={register("confirmPassword")}
                error={errors.confirmPassword?.message}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary text-primary-foreground rounded-md py-3 font-semibold text-base hover:bg-btn-primary-hover active:bg-btn-primary-selected transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Saving…" : "Change Password"}
            </button>
          </form>
        ) : (
          <div className="flex flex-col items-center w-full text-center">
            <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mb-4">
              <svg width="40" height="40" fill="none" viewBox="0 0 24 24">
                <path
                  d="M20 6L9.5 17L4 11.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-green-500"
                />
              </svg>
            </div>
            <h2 className="font-bold text-2xl mb-2">Password Updated</h2>
            <p className="text-gray-500 text-sm mb-6">
              Your password has been updated successfully. You can now log in with your new password.
            </p>
            <button
              onClick={handleGoToDashboard}
              className="w-full bg-primary text-primary-foreground rounded-md py-3 font-semibold text-base hover:bg-btn-primary-hover active:bg-btn-primary-selected transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Go to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ResetPasswordPageWithErrorBoundary: React.FC = () => (
  <ErrorBoundary>
    <ResetPasswordPage />
  </ErrorBoundary>
);

export default ResetPasswordPageWithErrorBoundary;
